/**
 * <cba-cart-feedback> — the "Added to cart" toast that flies into the
 * header's cart icon (snippets/cba-cart-feedback, once a page from the
 * layout).
 *
 *   <cba-cart-feedback class="cba-cart-feedback" role="status" aria-live="polite">
 *     [data-cba-feedback-thumb] … [data-cba-feedback-title]
 *   </cba-cart-feedback>
 *
 * - Listens for Shopify's shopify:cart:lines-update with action "add"
 *   (DESIGN-PLAN 7.G2), whoever added; stays silent while the cart drawer is
 *   open, and when the cart declined the add.
 * - The header's count waits for the toast: when an add starts it asks the
 *   header to hold its count (cba:cart:badge-hold), and the toast's landing
 *   releases it (cba:cart:badge-sync), so the header shows the cart's real
 *   count as the product reaches the icon. Both are events on document; it
 *   was a flag on window (DESIGN-PLAN 7.G6, shell audit S18.2).
 * - Reduced motion: a brief confirmation, the count at once, no flight.
 * - Every listener and timer goes when the element leaves the page.
 */
if (!customElements.get('cba-cart-feedback')) {
  const FLIGHT_MS = 600; /* must match .is-flying transition duration in CSS */
  const HOLD_MS = 1000; /* how long the popup sits before it flies */
  const SHAKE_MS = 450;
  const POP_MS = 300;

  class CbaCartFeedback extends HTMLElement {
    connectedCallback() {
      /* The thumbnail <img> is made when a product image arrives: an <img>
         without a src is invalid HTML (DESIGN-PLAN 2.5). */
      this.imgEl = null;
      this.thumbEl = this.querySelector('[data-cba-feedback-thumb]');
      this.titleEl = this.querySelector('[data-cba-feedback-title]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.busy = false;
      this.timers = [];
      this.abort = new AbortController();

      document.addEventListener('shopify:cart:lines-update', (e) => {
        if (e.action !== 'add' || !e.promise) return;
        /* Drawer already open → it shows the change; stay silent. */
        const drawer = document.querySelector('cba-cart-drawer');
        if (drawer && drawer.isOpen) return;
        /* Hold the header's count from the start, so it moves when the
           product lands, not when the cart answers. */
        document.dispatchEvent(new CustomEvent('cba:cart:badge-hold'));
        e.promise.then((result) => {
          if (!this.isConnected) return;
          if (result && result.userErrors && result.userErrors.length) { this.release(); return; }
          const item = result && result.detail && result.detail.items ? result.detail.items[0] : null;
          /* Coalesce rapid adds: restart with the newest. */
          if (this.busy) { this.clearTimers(); this.resetToast(); }
          this.busy = true;
          this.show({ image: item && item.image, title: item && (item.product_title || item.title) });
        }, () => this.release());
      }, { signal: this.abort.signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.clearTimers();
    }

    later(fn, ms) { this.timers.push(setTimeout(fn, ms)); }

    clearTimers() { this.timers.forEach(clearTimeout); this.timers = []; }

    /* Both the mobile and desktop headers carry [data-cba-open-cart]; the
       one on screen (offsetParent is null for the display: none one). */
    visibleCartIcon() {
      const icons = Array.from(document.querySelectorAll('[data-cba-open-cart]'));
      return icons.find((icon) => icon.offsetParent !== null) || icons[0] || null;
    }

    /* The header shows the count it held back. */
    release() {
      document.dispatchEvent(new CustomEvent('cba:cart:badge-sync'));
    }

    /* Start a CSS animation again: off, a reflow, on; off again when done. */
    replay(el, className, ms) {
      el.classList.remove(className);
      void el.offsetWidth;
      el.classList.add(className);
      this.later(() => el.classList.remove(className), ms + 60);
    }

    resetToast() {
      this.classList.remove('is-visible', 'is-flying');
      this.style.transform = '';
      this.style.opacity = '';
    }

    /* Landing: the header shows the real count, it pops and the icon shakes. */
    land(icon) {
      this.release();
      document.querySelectorAll('[data-cba-cart-badge]').forEach((badge) => this.replay(badge.querySelector('[data-cba-cart-badge-text]') || badge, 'cba-cart-count-pop', POP_MS));
      if (icon) this.replay(icon, 'cba-cart-icon-shake', SHAKE_MS);
      this.busy = false;
    }

    fly() {
      const icon = this.visibleCartIcon();
      if (!icon) { this.resetToast(); this.land(null); return; }
      const t = this.getBoundingClientRect();
      const c = icon.getBoundingClientRect();
      const dx = (c.left + c.width / 2) - (t.left + t.width / 2);
      const dy = (c.top + c.height / 2) - (t.top + t.height / 2);

      this.classList.remove('is-visible');
      this.classList.add('is-flying');
      void this.offsetWidth; /* commit before applying the flight transform */
      this.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(0.15)';
      this.style.opacity = '0';

      let landed = false;
      const onEnd = (e) => {
        if (landed || (e && e.propertyName && e.propertyName !== 'transform')) return;
        landed = true;
        this.removeEventListener('transitionend', onEnd);
        this.resetToast();
        this.land(icon);
      };
      this.addEventListener('transitionend', onEnd);
      this.later(onEnd, FLIGHT_MS + 150); /* fallback if transitionend is missed */
    }

    show({ image, title }) {
      if (this.thumbEl) {
        if (image) {
          if (!this.imgEl) {
            this.imgEl = document.createElement('img');
            this.imgEl.className = 'cba-img-cover';
            this.imgEl.alt = '';
            this.imgEl.width = 96;
            this.imgEl.height = 96;
            this.thumbEl.appendChild(this.imgEl);
          }
          this.imgEl.src = image;
        }
        this.thumbEl.style.display = image ? '' : 'none';
      }
      if (this.titleEl) this.titleEl.textContent = title || '';

      this.classList.add('is-visible');
      if (this.reduceMotion) {
        this.release();
        this.later(() => { this.classList.remove('is-visible'); this.busy = false; }, HOLD_MS);
        return;
      }
      this.later(() => this.fly(), HOLD_MS);
    }
  }

  customElements.define('cba-cart-feedback', CbaCartFeedback);
}
